import { Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'

/** Modelos oferecidos, na ordem da tela. A chave aponta para `agents.persona.templates.items.<key>`. */
const INSTRUCTION_TEMPLATE_KEYS = [
  'general',
  'techSupport',
  'sales',
  'ecommerce',
  'scheduling',
  'billing',
] as const

type InstructionTemplateKey = (typeof INSTRUCTION_TEMPLATE_KEYS)[number]

interface InstructionTemplatesProps {
  /** Texto atual das instruções: se houver algo escrito, pedimos confirmação antes de substituir. */
  readonly current: string
  /** Nome da organização, inserido no modelo; ausente usa um genérico ("nossa empresa"). */
  readonly companyName?: string | undefined
  readonly disabled?: boolean
  readonly onApply: (text: string) => void
}

/**
 * Atalho para não começar as Instruções do zero: modelos prontos por tipo de negócio, já alinhados ao produto
 * (só responder com base no conhecimento, transferir para humano quando não souber, nunca pedir senha/cartão).
 * O texto vira ponto de partida editável — nunca é aplicado por cima do que o usuário escreveu sem confirmação.
 */
export function InstructionTemplates({
  current,
  companyName,
  disabled = false,
  onApply,
}: InstructionTemplatesProps) {
  const { t } = useTranslation()
  const [pending, setPending] = useState<InstructionTemplateKey | null>(null)

  const company =
    companyName !== undefined && companyName.trim() !== ''
      ? companyName.trim()
      : t('agents.persona.templates.fallbackCompany')

  const names: Readonly<Record<InstructionTemplateKey, string>> = {
    general: t('agents.persona.templates.items.general.name'),
    techSupport: t('agents.persona.templates.items.techSupport.name'),
    sales: t('agents.persona.templates.items.sales.name'),
    ecommerce: t('agents.persona.templates.items.ecommerce.name'),
    scheduling: t('agents.persona.templates.items.scheduling.name'),
    billing: t('agents.persona.templates.items.billing.name'),
  }

  function textOf(key: InstructionTemplateKey): string {
    switch (key) {
      case 'general':
        return t('agents.persona.templates.items.general.text', { company })
      case 'techSupport':
        return t('agents.persona.templates.items.techSupport.text', { company })
      case 'sales':
        return t('agents.persona.templates.items.sales.text', { company })
      case 'ecommerce':
        return t('agents.persona.templates.items.ecommerce.text', { company })
      case 'scheduling':
        return t('agents.persona.templates.items.scheduling.text', { company })
      case 'billing':
        return t('agents.persona.templates.items.billing.text', { company })
    }
  }

  function choose(key: InstructionTemplateKey): void {
    if (current.trim() === '') {
      onApply(textOf(key))
      return
    }

    setPending(key)
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border-default bg-background-subtle p-3">
      <div className="flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-secondary">
          <Sparkles aria-hidden="true" className="h-3.5 w-3.5 text-status-info" />
          {t('agents.persona.templates.title')}
        </span>
        <span className="text-xs text-foreground-muted">{t('agents.persona.templates.hint')}</span>
      </div>

      <div
        role="group"
        aria-label={t('agents.persona.templates.title')}
        className="flex flex-wrap gap-1.5"
      >
        {INSTRUCTION_TEMPLATE_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            disabled={disabled}
            aria-pressed={pending === key}
            onClick={() => {
              choose(key)
            }}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs font-medium',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
              'disabled:cursor-not-allowed disabled:opacity-60',
              pending === key
                ? 'border-action-primary bg-background-default text-foreground-primary'
                : 'border-border-default bg-background-default text-foreground-secondary hover:border-action-primary hover:text-foreground-primary',
            )}
          >
            {names[key]}
          </button>
        ))}
      </div>

      {pending !== null ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-background-default p-2 text-xs text-foreground-primary"
        >
          <span>{t('agents.persona.templates.replaceQuestion', { name: names[pending] })}</span>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setPending(null)
              }}
            >
              {t('agents.persona.templates.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onApply(textOf(pending))
                setPending(null)
              }}
            >
              {t('agents.persona.templates.replace')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
